import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import {
  Storage as DatabaseIcon,
  TableChart as TableIcon,
  CloudUpload as UploadIcon,
  People as UsersIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { dashboardAPI } from "../services/api.js";
import { useAuth } from "../contexts/AuthContext.jsx";

const StatCard = ({ title, value, icon, color = "primary" }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          backgroundColor: `${color}.light`,
          color: `${color}.contrastText`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="h4" fontWeight="bold">
          {value?.toLocaleString() || 0}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

const UserViewBox = ({ userData }) => (
  <Card sx={{ mb: 3, border: '2px solid', borderColor: userData.is_admin ? 'warning.main' : 'primary.main' }}>
    <CardContent>
      {/* User Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            {userData.username}
          </Typography>
          {userData.is_admin && (
            <Chip 
              label="Admin" 
              color="warning" 
              size="small"
              icon={<PersonIcon />}
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">
          Schema: {userData.schema_name}
        </Typography>
      </Box>

      {/* User Statistics */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'primary.light', borderRadius: 1 }}>
            <Typography variant="h6" fontWeight="bold" color="primary.contrastText">
              {userData.statistics.total_tables}
            </Typography>
            <Typography variant="caption" color="primary.contrastText">
              Tables
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'secondary.light', borderRadius: 1 }}>
            <Typography variant="h6" fontWeight="bold" color="secondary.contrastText">
              {userData.statistics.total_uploads}
            </Typography>
            <Typography variant="caption" color="secondary.contrastText">
              Uploads
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'success.light', borderRadius: 1 }}>
            <Typography variant="h6" fontWeight="bold" color="success.contrastText">
              {userData.statistics.completed_uploads}
            </Typography>
            <Typography variant="caption" color="success.contrastText">
              Completed
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'info.light', borderRadius: 1 }}>
            <Typography variant="h6" fontWeight="bold" color="info.contrastText">
              {userData.statistics.total_records?.toLocaleString() || 0}
            </Typography>
            <Typography variant="caption" color="info.contrastText">
              Records
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* User Tables */}
      {userData.tables && userData.tables.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Tables ({userData.tables.length})
          </Typography>
          <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
            <Grid container spacing={1}>
              {userData.tables.map((table, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    sx={{
                      p: 1,
                      backgroundColor: 'grey.100',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.300'
                    }}
                  >
                    <Typography variant="body2" fontWeight="bold" noWrap>
                      {table.table_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {table.column_count} cols
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      )}

      {/* Recent Activity */}
      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'grey.300' }}>
        <Typography variant="body2" color="text.secondary">
          Recent uploads (7 days): {userData.recent_uploads || 0}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { user, isAdmin } = useAuth();

  const {
    data: overviewData,
    isLoading: overviewLoading,
    error: overviewError,
  } = useQuery({
    queryKey: ["dashboard-overview"],
    queryFn: dashboardAPI.getOverview,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const {
    data: healthData,
    isLoading: healthLoading,
    error: healthError,
  } = useQuery({
    queryKey: ["system-health"],
    queryFn: dashboardAPI.getHealth,
    refetchInterval: 60000, // Refresh every minute
  });

  if (overviewLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (overviewError) {
    return (
      <Alert severity="error">
        Failed to load dashboard data:{" "}
        {overviewError.response?.data?.error || overviewError.message}
      </Alert>
    );
  }

  const overview = overviewData?.data?.overview || {};
  const uploadStats = overviewData?.data?.upload_statistics || {};
  const dbStats = overviewData?.data?.database_statistics || [];
  const topTables = overviewData?.data?.top_tables || [];
  const health = healthData?.data?.health_status || {};
  const userViewboxes = overviewData?.data?.user_viewboxes || [];

  const isUserSpecific = overviewData?.data?.user_specific || false;
  const isAdminView = overviewData?.data?.is_admin_view || false;

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h4" gutterBottom fontWeight="bold">
          {isAdmin() ? "Admin Dashboard" : "My Dashboard"}
        </Typography>
        {isUserSpecific && (
          <Chip
            icon={<PersonIcon />}
            label="Personal View"
            color="primary"
            variant="outlined"
          />
        )}
      </Box>

      <Typography variant="body1" color="text.secondary" mb={3}>
        {isAdminView
          ? "Multi-user system overview with individual user data"
          : isAdmin()
          ? "System overview and statistics"
          : "Your personal activity and statistics"}
      </Typography>

      {/* Admin Multi-User Dashboard */}
      {isAdminView && userViewboxes.length > 0 ? (
        <Box>
          {/* System Overview Cards */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Users"
                value={overview.total_users}
                icon={<UsersIcon />}
                color="info"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Tables"
                value={overview.total_tables}
                icon={<TableIcon />}
                color="secondary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Uploads"
                value={overview.total_uploads}
                icon={<UploadIcon />}
                color="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Records"
                value={overview.total_records}
                icon={<DatabaseIcon />}
                color="primary"
              />
            </Grid>
          </Grid>

          {/* System Statistics */}
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {/* Upload Statistics */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    System Upload Statistics
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    {Object.entries(uploadStats).map(([status, count]) => (
                      <Box
                        key={status}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ textTransform: "capitalize" }}
                        >
                          {status.replace("_", " ")}:
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {count}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Database Statistics */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Database Statistics
                  </Typography>
                  <Box sx={{ mt: 2, maxHeight: 200, overflowY: "auto" }}>
                    {dbStats.map((db, index) => (
                      <Box
                        key={index}
                        sx={{
                          mb: 2,
                          p: 1,
                          backgroundColor: "grey.50",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold">
                          {db.database_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {db.table_count} tables,{" "}
                          {db.record_count?.toLocaleString()} records
                        </Typography>
                      </Box>
                    ))}
                    {dbStats.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No databases found
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          {/* User Viewboxes (moved below statistics) */}
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 4, mb: 3 }}>
            User Data Overview
          </Typography>
          <Grid container spacing={3}>
            {userViewboxes.map((userData, index) => (
              <Grid item xs={12} lg={6} key={userData.user_id || index}>
                <UserViewBox userData={userData} />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        /* Regular Dashboard */
        <Box>
          {/* Overview Cards */}
          <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Databases"
            value={overview.total_databases}
            icon={<DatabaseIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Tables"
            value={overview.total_tables}
            icon={<TableIcon />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Uploads"
            value={overview.total_uploads}
            icon={<UploadIcon />}
            color="success"
          />
        </Grid>
        {isAdmin() && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Users"
              value={overview.total_users}
              icon={<UsersIcon />}
              color="info"
            />
          </Grid>
        )}
      </Grid>

      <Grid container spacing={3}>
        {/* Upload Statistics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {isUserSpecific ? "My Upload Statistics" : "Upload Statistics"}
              </Typography>
              <Box sx={{ mt: 2 }}>
                {Object.entries(uploadStats).map(([status, count]) => (
                  <Box
                    key={status}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ textTransform: "capitalize" }}
                    >
                      {status.replace("_", " ")}:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {count}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Database Statistics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {isUserSpecific
                  ? "My Database Statistics"
                  : "Database Statistics"}
              </Typography>
              <Box sx={{ mt: 2, maxHeight: 200, overflowY: "auto" }}>
                {dbStats.map((db, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 2,
                      p: 1,
                      backgroundColor: "grey.50",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      {db.database_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {db.table_count} tables,{" "}
                      {db.record_count?.toLocaleString()} records
                    </Typography>
                  </Box>
                ))}
                {dbStats.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No databases found
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Tables */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {isUserSpecific
                  ? "My Top Tables by Record Count"
                  : "Top Tables by Record Count"}
              </Typography>
              <Box sx={{ mt: 2 }}>
                {topTables.map((table, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                      p: 1,
                      backgroundColor: index < 3 ? "primary.light" : "grey.50",
                      borderRadius: 1,
                      color:
                        index < 3 ? "primary.contrastText" : "text.primary",
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        #{index + 1}. {table.database_name}.{table.table_name}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {table.column_count
                          ? `${table.column_count} columns`
                          : table.upload_count
                          ? `${table.upload_count} uploads`
                          : "N/A"}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="bold">
                      {table.record_count?.toLocaleString()} records
                    </Typography>
                  </Box>
                ))}
                {topTables.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No tables found
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
